import PropTypes from 'prop-types';
import React, { useEffect, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { prepareReportChartColors } from '../../../../services/report/chart/colorProcessor';
import { postGASForURL } from '../../../../services/report/reportApiService';
import { cleanReportData, getReportPayload, prepareReportPayload } from '../../../../stores/actions/report';
import { toast } from 'react-toastify';

/**
 * Componente contextual para exportacao de relatorios, acoplado ao contexto
 * e regras de negocio do sistema
 */
const propTypes = {
  onReportDone: PropTypes.func,
  onReportFail: PropTypes.func,
};
function ReferenciableReportProcessing(props, ref) {
  // state from props
  const { onReportDone, onReportFail } = props;

  // store
  const dispatch = useDispatch();
  const { rawPayload, processedPayload } = useSelector((state) => state.report);
  const { token } = useSelector((state) => state.access.payload);

  // Aqui nós armazenamos as cores da pergunta atual, caso exista, apenas para
  // reduzir uma requisição.
  const {
    // Payload de cores do gráfico simples
    chartConfigData: { payload: { chartsColors: simpleChartColorConfig }},
    evolutiveChartConfigData: { payload: { chartsColors: evolutiveChartColorConfig }},
  } = useSelector((state) => state.charts);

  // Armazenando o id da pergunta atual
  const { question: activeQuestion } = useSelector((state) => state.activeSurvey);
  const activeQuestionId = (activeQuestion && activeQuestion.id) || undefined;

  const reportCurrentColorsConfig = [
    {
      type: 'simpleChart',
      colors: simpleChartColorConfig,
      questionId: activeQuestionId,
    },
    {
      type: 'evolutiveChart',
      colors: evolutiveChartColorConfig,
      questionId: activeQuestionId,
    }
  ];

  /**
      * Verifica se o payload bruto do relatorio foi
      * carregado, em caso positivo, inicia o pos-processamento
      * do mesmo
      */
  function processReportRawPayload() {
    if (!rawPayload.running && rawPayload.data) {
      // Primeiro prepara as cores dos gráficos, e quando as cores estiverem
      // prontas, então a preparação do payload do report é iniciada.
      prepareReportChartColors(rawPayload.data,  activeQuestionId, reportCurrentColorsConfig)
        .then((reportColorConfig) => {
          prepareReportPayload(rawPayload.data, token, dispatch, reportColorConfig);
        })
        // Caso ocorra um erro, ele fará o mesmo processamento, porém irá utilizar as cores do tema da aplicação.
        .catch(err => {
          prepareReportPayload(rawPayload.data, token, dispatch, [])
        })
    }
  }

  /**
     * Verifica se o report rawPayload possui erros
     * em caso positivo, exibe a mensagem e cancela
     * o carregamento do payload
     */
  function checkReportRawPayloadErrors() {
    if (rawPayload.error) {
      onReportFail && onReportFail(rawPayload.error);
    }
  }

  /**
     * Verifica se o pos-processamento do payload falhou, em caso
     * positivo notifica o parent component e limpa os dados do
     * relatorio, encerrando o carregamento
     */
  function checkProcessedPayloadErrors() {
    if (processedPayload.error) {
      onReportFail && onReportFail(processedPayload.error);
      cleanReportData(dispatch);
    }
  }

  /**
     * Verifica se o relatorio ainta esta sendo processado
     * e notifica o parent component em caso de sucesso
     */
  async function checkReportIsProcessing() {
    if (!rawPayload.running && !processedPayload.running && processedPayload.data) {
      postGASForURL(processedPayload.data).then((gasUrl) => {
        onReportDone && onReportDone(gasUrl);
      }).catch((error) => {
        onReportFail && onReportFail(error);
      }).finally(() => cleanReportData(dispatch));
    }
  }

  /**
     * Inicia o processo de construcao do payload do relatorio.
     * Primeiramente, recuperando os dados brutos do servidor
     * e posteriormente disparando a aplicacao do pos-processamento
     * nos dados
     */
  function buildReportPayload(preProcessedReportData) {
    getReportPayload(preProcessedReportData, dispatch);
  }

  /**
     * Callback executado quando o processedPayload
     * eh alterado
     */
  useEffect(() => {
    checkProcessedPayloadErrors();
    checkReportIsProcessing();
  }, [processedPayload]);

  /**
     * Callback executado quando o rawPayload
     */
  useEffect(() => {
    checkReportRawPayloadErrors();
    processReportRawPayload();
  }, [rawPayload]);

  useImperativeHandle(ref, () => ({
    buildReportPayload: (preProcessedReportData) => {
      buildReportPayload(preProcessedReportData);
    },
  }));
}

const ReportProcessing = React.forwardRef(ReferenciableReportProcessing);
ReportProcessing.propTypes = propTypes;
export default ReportProcessing;
